import '../textEffects.css';

const DECODE_CHARACTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
const DECODE_DURATION_MULTIPLIER = 5;
const MIN_EMOJI_DECODE_DURATION = 1250;
const EMOJI_DURATION_EXTENSION = 120;
const EMOJI_REVEAL_RATIO = 0.88;
const EMOJI_REVEAL_END_GUARD = 180;
const EMOJI_SHUFFLE_INTERVAL_START = 55;
const EMOJI_SHUFFLE_INTERVAL_END = 180;
const EMOJI_DECODE_SEGMENTS = [
  '🌘',
  '🌙',
  '🩸',
  '💀',
  '⛓',
  '♨️',
  '🔋',
  '👾',
  '💊',
  '🕷️',
  '🕸️',
  '🖤',
  '⚔️',
  '🗡️',
  '🔥',
];
const EMOJI_SEGMENT_PATTERN = /\p{Extended_Pictographic}/u;

function splitIntoGraphemes(text) {
  if (!text) {
    return [];
  }

  if (typeof Intl !== 'undefined' && typeof Intl.Segmenter === 'function') {
    const segmenter = new Intl.Segmenter(undefined, { granularity: 'grapheme' });
    return Array.from(segmenter.segment(text), ({ segment }) => segment);
  }

  return Array.from(text);
}

function isWhitespaceSegment(segment) {
  return /^\s+$/u.test(segment);
}

function isEmojiSegment(segment) {
  return EMOJI_SEGMENT_PATTERN.test(segment);
}

function getOverlayJustifyContent(element) {
  const textAlign = window.getComputedStyle(element).textAlign;

  if (textAlign === 'center') {
    return 'center';
  }

  if (textAlign === 'right' || textAlign === 'end') {
    return 'flex-end';
  }

  return 'flex-start';
}

function shouldUseMeasuredWrapDecode(element) {
  if (!element?.classList?.contains('page-title')) {
    return false;
  }

  const whiteSpace = window.getComputedStyle(element).whiteSpace || '';
  return !whiteSpace.includes('nowrap') && !whiteSpace.startsWith('pre');
}

function getElementFontShorthand(computedStyle) {
  if (
    computedStyle.font &&
    computedStyle.font !== 'normal normal normal normal 16px / normal Times New Roman'
  ) {
    return computedStyle.font;
  }

  return [
    computedStyle.fontStyle,
    computedStyle.fontVariant,
    computedStyle.fontWeight,
    computedStyle.fontStretch,
    `${computedStyle.fontSize}${
      computedStyle.lineHeight !== 'normal' ? ` / ${computedStyle.lineHeight}` : ''
    }`,
    computedStyle.fontFamily,
  ]
    .filter(Boolean)
    .join(' ');
}

function getTextMeasureContext(element) {
  const computedStyle = window.getComputedStyle(element);
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');

  if (!context) {
    return null;
  }

  context.font = getElementFontShorthand(computedStyle);

  if ('fontKerning' in context) {
    context.fontKerning = computedStyle.fontKerning || 'normal';
  }

  const parsedLetterSpacing = Number.parseFloat(computedStyle.letterSpacing);
  const letterSpacing = Number.isFinite(parsedLetterSpacing) ? parsedLetterSpacing : 0;

  return { context, letterSpacing };
}

function measureSlotWidths(element, segments) {
  const measureData = getTextMeasureContext(element);
  if (!measureData) {
    return segments.map(() => 0);
  }

  const { context, letterSpacing } = measureData;
  const widths = [];
  let previousWidth = 0;

  for (let index = 0; index < segments.length; index += 1) {
    const partialText = segments.slice(0, index + 1).join('');
    const measuredWidth = context.measureText(partialText).width + letterSpacing * index;
    widths.push(Math.max(measuredWidth - previousWidth, 0));
    previousWidth = measuredWidth;
  }

  return widths;
}

function buildCharacterPools(element, slotWidths, segments) {
  const measureData = getTextMeasureContext(element);
  if (!measureData) {
    return slotWidths.map((_, index) =>
      isEmojiSegment(segments[index]) ? EMOJI_DECODE_SEGMENTS : splitIntoGraphemes(DECODE_CHARACTERS)
    );
  }

  const { context } = measureData;
  const widthCache = new Map();
  const alphaNumericPool = splitIntoGraphemes(DECODE_CHARACTERS);

  return slotWidths.map((slotWidth, index) => {
    const candidateSegments = isEmojiSegment(segments[index]) ? EMOJI_DECODE_SEGMENTS : alphaNumericPool;
    const pool = candidateSegments.filter((candidate) => {
      if (!widthCache.has(candidate)) {
        widthCache.set(candidate, context.measureText(candidate).width);
      }
      return widthCache.get(candidate) <= slotWidth + 0.5;
    });

    return pool.length
      ? pool
      : candidateSegments.filter(Boolean).length
        ? candidateSegments
        : alphaNumericPool;
  });
}

function normalizeRenderedSegment(segment) {
  if (isWhitespaceSegment(segment)) {
    return '\u00A0';
  }

  return segment;
}

function getEffectiveDecodeDuration(segments, duration) {
  const effectiveDuration = duration * DECODE_DURATION_MULTIPLIER;

  if (!segments.some(isEmojiSegment)) {
    return effectiveDuration;
  }

  return Math.max(effectiveDuration + EMOJI_DURATION_EXTENSION, MIN_EMOJI_DECODE_DURATION);
}

function clampNumber(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function getElementEmojiRevealOffset(element) {
  const rawValue = element?.dataset?.decodeEmojiOffset;
  const parsedValue = Number.parseFloat(rawValue);
  return Number.isFinite(parsedValue) ? parsedValue : 0;
}

function getEmojiRevealTime(totalDuration, offset = 0) {
  const baseRevealTime = Math.max(MIN_EMOJI_DECODE_DURATION, totalDuration * EMOJI_REVEAL_RATIO);
  const maxRevealTime = Math.max(MIN_EMOJI_DECODE_DURATION, totalDuration - EMOJI_REVEAL_END_GUARD);

  return clampNumber(baseRevealTime + offset, MIN_EMOJI_DECODE_DURATION, maxRevealTime);
}

function getRevealTimes(segments, totalDuration, emojiRevealOffset = 0) {
  const nonWhitespaceSegments = segments.filter((segment) => !isWhitespaceSegment(segment));
  const revealableCount = Math.max(nonWhitespaceSegments.length - 1, 1);
  let revealableIndex = 0;

  return segments.map((segment, index) => {
    if (isWhitespaceSegment(segment)) {
      return 0;
    }

    if (isEmojiSegment(segment)) {
      return getEmojiRevealTime(totalDuration, emojiRevealOffset);
    }

    const baseRevealTime = (totalDuration * revealableIndex) / revealableCount;
    revealableIndex += 1;
    return baseRevealTime;
  });
}

function getEmojiShuffleInterval(elapsed, revealTime) {
  const progress = Math.min(1, elapsed / Math.max(revealTime, 1));
  const slowdownProgress = Math.pow(progress, 2.35);

  return (
    EMOJI_SHUFFLE_INTERVAL_START +
    (EMOJI_SHUFFLE_INTERVAL_END - EMOJI_SHUFFLE_INTERVAL_START) * slowdownProgress
  );
}

function getNextPoolSegment(pool, fallbackSegment, currentSegment) {
  if (!pool || pool.length === 0) {
    return fallbackSegment;
  }

  if (pool.length === 1) {
    return pool[0];
  }

  let nextSegment = pool[Math.floor(Math.random() * pool.length)] || fallbackSegment;
  if (nextSegment === currentSegment) {
    nextSegment = pool.find((candidate) => candidate !== currentSegment) || nextSegment;
  }

  return nextSegment;
}

function createDecodeLayout(element, text) {
  const segments = splitIntoGraphemes(text);
  const slotWidths = measureSlotWidths(element, segments);
  const characterPools = buildCharacterPools(element, slotWidths, segments);
  const hadAriaLabel = element.hasAttribute('aria-label');
  const originalAriaLabel = element.getAttribute('aria-label');

  if (!hadAriaLabel) {
    element.setAttribute('aria-label', text);
  }

  element.classList.add('decode-lock');
  element.textContent = '';

  const ghost = document.createElement('span');
  ghost.className = 'decode-ghost';
  ghost.textContent = text;
  ghost.setAttribute('aria-hidden', 'true');

  const overlay = document.createElement('span');
  overlay.className = 'decode-overlay';
  overlay.style.justifyContent = getOverlayJustifyContent(element);
  overlay.setAttribute('aria-hidden', 'true');

  const track = document.createElement('span');
  track.className = 'decode-track';
  overlay.appendChild(track);

  const slots = segments.map((segment, index) => {
    const slot = document.createElement('span');
    slot.className = 'decode-slot';
    slot.style.width = `${Math.max(slotWidths[index], 0)}px`;
    slot.textContent = normalizeRenderedSegment(segment);
    track.appendChild(slot);
    return slot;
  });

  element.appendChild(ghost);
  element.appendChild(overlay);

  return {
    segments,
    slots,
    characterPools,
    cleanup: () => {
      element.classList.remove('decode-lock');
      element.textContent = text;
      if (hadAriaLabel) {
        element.setAttribute('aria-label', originalAriaLabel || '');
      } else {
        element.removeAttribute('aria-label');
      }
    },
  };
}

function createMeasuredDecodeLayout(element, text) {
  const segments = splitIntoGraphemes(text);
  const hadAriaLabel = element.hasAttribute('aria-label');
  const originalAriaLabel = element.getAttribute('aria-label');

  if (!hadAriaLabel) {
    element.setAttribute('aria-label', text);
  }

  element.classList.add('decode-lock', 'decode-lock--measured');
  element.textContent = '';

  const ghost = document.createElement('span');
  ghost.className = 'decode-ghost decode-ghost--measured';
  ghost.setAttribute('aria-hidden', 'true');

  const ghostTextNode = document.createTextNode(text);
  ghost.appendChild(ghostTextNode);
  element.appendChild(ghost);

  const overlay = document.createElement('span');
  overlay.className = 'decode-overlay decode-overlay--measured';
  overlay.setAttribute('aria-hidden', 'true');
  element.appendChild(overlay);

  const ghostRect = ghost.getBoundingClientRect();
  const slotWidths = segments.map(() => 0);
  const slots = [];
  let codeUnitOffset = 0;

  segments.forEach((segment, index) => {
    const startOffset = codeUnitOffset;
    const endOffset = startOffset + segment.length;
    codeUnitOffset = endOffset;

    if (isWhitespaceSegment(segment)) {
      slots[index] = null;
      return;
    }

    const range = document.createRange();
    range.setStart(ghostTextNode, startOffset);
    range.setEnd(ghostTextNode, endOffset);

    const rect = range.getBoundingClientRect();
    slotWidths[index] = rect.width;

    const slot = document.createElement('span');
    slot.className = 'decode-slot decode-slot--measured';
    slot.style.left = `${rect.left - ghostRect.left}px`;
    slot.style.top = `${rect.top - ghostRect.top}px`;
    slot.style.width = `${Math.max(rect.width, 0)}px`;
    slot.style.height = `${Math.max(rect.height, 0)}px`;
    slot.textContent = normalizeRenderedSegment(segment);
    overlay.appendChild(slot);
    slots[index] = slot;
  });

  const characterPools = buildCharacterPools(element, slotWidths, segments);

  return {
    segments,
    slots,
    characterPools,
    cleanup: () => {
      element.classList.remove('decode-lock', 'decode-lock--measured');
      element.textContent = text;
      if (hadAriaLabel) {
        element.setAttribute('aria-label', originalAriaLabel || '');
      } else {
        element.removeAttribute('aria-label');
      }
    },
  };
}

export function decodeTextLocked(element, text, duration = 1100) {
  if (!element || !text) {
    return () => {};
  }

  const layout = shouldUseMeasuredWrapDecode(element)
    ? createMeasuredDecodeLayout(element, text)
    : createDecodeLayout(element, text);
  const { segments, slots, characterPools, cleanup } = layout;
  const totalDuration = getEffectiveDecodeDuration(segments, duration);
  const revealTimes = getRevealTimes(segments, totalDuration, getElementEmojiRevealOffset(element));
  const startTime = Date.now();
  const slotStates = segments.map((segment) => ({
    currentSegment: segment,
    nextShuffleAt: 0,
  }));
  const intervalId = setInterval(() => {
    const elapsed = Date.now() - startTime;

    segments.forEach((segment, index) => {
      if (!slots[index]) {
        return;
      }

      if (elapsed >= revealTimes[index] || isWhitespaceSegment(segment)) {
        slots[index].textContent = normalizeRenderedSegment(segment);
        slotStates[index].currentSegment = segment;
        return;
      }

      const pool = characterPools[index];
      if (isEmojiSegment(segment)) {
        if (elapsed < slotStates[index].nextShuffleAt) {
          return;
        }

        const nextSegment = getNextPoolSegment(pool, segment, slotStates[index].currentSegment);
        slotStates[index].currentSegment = nextSegment;
        slotStates[index].nextShuffleAt =
          elapsed + getEmojiShuffleInterval(elapsed, revealTimes[index]);
        slots[index].textContent = normalizeRenderedSegment(nextSegment);
        return;
      }

      const nextSegment = getNextPoolSegment(pool, segment, slotStates[index].currentSegment);
      slotStates[index].currentSegment = nextSegment;
      slots[index].textContent = normalizeRenderedSegment(nextSegment);
    });

    if (elapsed >= totalDuration) {
      clearInterval(intervalId);
      segments.forEach((segment, index) => {
        slots[index].textContent = normalizeRenderedSegment(segment);
      });
    }
  }, 50);

  return () => {
    clearInterval(intervalId);
    cleanup();
  };
}

export function applyRandomFlicker(
  element,
  minTime = 50,
  maxTime = 200,
  highOpacity = 1,
  lowOpacity = 0.8
) {
  if (!element) {
    return () => {};
  }

  let timeoutId;
  let cancelled = false;

  const randomFlicker = () => {
    if (cancelled) {
      return;
    }

    const randomTime = Math.random() * (maxTime - minTime) + minTime;
    const randomOpacity =
      Math.random() < 0.1
        ? lowOpacity
        : Math.random() * (highOpacity - lowOpacity) + lowOpacity;

    element.style.transition = `opacity ${randomTime}ms`;
    element.style.opacity = randomOpacity;

    timeoutId = setTimeout(randomFlicker, randomTime);
  };

  randomFlicker();

  return () => {
    cancelled = true;
    clearTimeout(timeoutId);
  };
}

export function runAfterFontsReady(callback) {
  let cancelled = false;

  const start = () => {
    if (!cancelled) {
      callback();
    }
  };

  if (typeof document !== 'undefined' && document.fonts?.ready) {
    document.fonts.ready.then(start).catch(start);
  } else {
    start();
  }

  return () => {
    cancelled = true;
  };
}
